// referrerFromUrl — Phase 8 Task 8-5 (定着率メトリクス計装) 用の招待リンク
// 識別子 (`?ref=...`) URL クエリ抽出 helper.
//
// A4 修理 (analytics-operations-2026-08-13.md §2 表 A4): `?ref=...` を貼る
// 導線が web 内に一つも無く、90 日間 `referrer` が全件 null だった。この
// ファイルの `readReferrerFromSearch` 自体は完成済みで壊れていない — 呼び出す
// 側 (= URL 生成) が存在しなかっただけ。そこで `document.referrer` を
// フォールバック値として追加する (§1.4 実測: t.co / google.com /
// legacy.takamin.com / bing.com / oekakiart.net 等のチャネル分類が取れる).
//
// Purpose:
// - 入室画面 (HomePage / ReJoinScreen / `?action=create` splash) を開いたとき
//   の `window.location.search` から `ref` パラメータを抽出する (優先).
// - `?ref=` が無ければ `document.referrer` を**オリジンに正規化**して使う
//   (A4). フルパスはトークンや個人ページ URL を含みうるため PII 混入防止上
//   必須 (analytics-operations-2026-08-13.md §1.4「この制約はプライバシー的
//   にはむしろ望ましい」).
// - 抽出値は `room.join.referrer` (optional, max 64) として送信される.
//   サーバ analytics layer で raw 値のまま保管され、ファネル「招待 → 入室 →
//   描画 → リピート」の入口計測に使われる. PII を含めない slug / オリジン
//   想定. (analytics.md §3.1 / §8、protocol.md `room.join.referrer`)
//
// **読まない値**:
// - 空文字 (= `?ref=` のみ) → `null`
// - 64 文字超 → `null` (server schema が reject するので無効値を送らない)
// - URL parse 不可 (= window.location.search / document.referrer が異常) →
//   `null`
// - 自オリジン (= oekaki.takamin.com 自身) からの `document.referrer` →
//   `null` (内部遷移、招待元ではない)
//
// **トリミング方針**:
// - 先頭 / 末尾の whitespace は trim する (URL encode された空白等の事故対策).
// - 切り捨て (slice 64) ではなく **拒否** (`null`) する.  64 文字超は通常
//   slug ではなく長文 referrer URL や PII の流入なので、欠片を analytics に
//   残すより捨てる方が安全 (analytics.md §3.1 「PII 流入防止」).
//
// 純粋関数 (input: string, output: string | null). DOM 触らないので
// SessionProvider 側で `window.location.search` / `document.referrer` を
// 渡して使う.

const REFERRER_MAX_LENGTH = 64;

/**
 * Parse `?ref=...` from a URL search string.
 *
 * @param search - The `window.location.search` value (with or without `?`).
 *                 An empty string / undefined yields `null`.
 * @returns The trimmed referrer slug (1..64 chars), or `null` if absent /
 *          empty / over-length / unparseable.
 */
export const readReferrerFromSearch = (search: string | undefined): string | null => {
  if (typeof search !== 'string' || search.length === 0) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  const raw = params.get('ref');
  if (raw === null) return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length > REFERRER_MAX_LENGTH) return null;
  return trimmed;
};

/**
 * Normalize a raw `document.referrer` value down to just its origin
 * (`scheme://host[:port]`), stripping path / query / fragment — those can
 * carry tokens or personal-page URLs, so keeping only the origin is a hard
 * PII-avoidance requirement (A4, analytics-operations-2026-08-13.md §1.4).
 *
 * Returns `null` when:
 * - `raw` is empty / undefined / not a parseable absolute URL
 * - the scheme isn't `http:` / `https:` (e.g. `android-app://`, `file://`)
 * - the resulting origin equals `currentOrigin` (= internal navigation
 *   within oekaki.takamin.com itself, not an external referrer)
 * - the origin exceeds 64 chars (defensive — real-world origins are never
 *   this long, but the server schema caps at 64 and we reject rather than
 *   truncate, matching `readReferrerFromSearch`'s policy)
 *
 * Pure function — no DOM access. `readReferrerFromCurrentUrl` supplies the
 * live `document.referrer` / `window.location.origin` values.
 */
export const normalizeReferrerOrigin = (
  raw: string | undefined,
  currentOrigin: string | undefined,
): string | null => {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  const origin = url.origin;
  if (origin.length === 0 || origin.length > REFERRER_MAX_LENGTH) return null;
  if (typeof currentOrigin === 'string' && origin === currentOrigin) return null;
  return origin;
};

/**
 * Convenience wrapper that reads `document.referrer` + `window.location.origin`
 * (or returns `null` in non-DOM environments) and normalizes via
 * `normalizeReferrerOrigin`.
 */
export const readNormalizedDocumentReferrer = (): string | null => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return null;
  return normalizeReferrerOrigin(document.referrer, window.location.origin);
};

/**
 * Convenience wrapper that reads from `window.location.search` (or returns
 * `null` in non-DOM environments).  Pure thin shim — call sites are
 * SessionProvider mount + (potentially) early entry pages.
 *
 * A4 (analytics-operations-2026-08-13.md §2 表 A4): `?ref=...` が無ければ
 * `document.referrer` の正規化オリジンにフォールバックする. `?ref=` が既存の
 * 明示的な意図表明 (招待リンク) なので優先度は変えない — 既存挙動を壊さない.
 */
export const readReferrerFromCurrentUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  const fromQuery = readReferrerFromSearch(window.location.search);
  if (fromQuery !== null) return fromQuery;
  return readNormalizedDocumentReferrer();
};
